import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import {
  Users, Database, RefreshCw, TrendingUp, TrendingDown, AlertCircle,
  CalendarClock, ArrowUpRight, Eye, CheckCircle2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { formatDate, formatDateTime } from "../lib/format";

const SC1_EMAIL = "rhea.sharma@getwork.org";

const Stat = ({ icon: Icon, label, value, sub, accent = "indigo", testid }) => (
  <Card data-testid={testid} className="p-5 border-slate-200 hover:border-indigo-200 transition-colors">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-400">{label}</div>
        <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-lg bg-${accent}-50 text-${accent}-600 flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAs, setViewAs] = useState("super_custodian");

  useEffect(() => {
    setLoading(true);
    const isSC1 = user?.role === "super_custodian" && user?.email === SC1_EMAIL;
    const params = isSC1 && viewAs !== "super_custodian" ? { view_as: viewAs } : {};
    api.get("/dashboard", { params })
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [viewAs, user?.role, user?.email]);

  if (loading) return <Layout><div className="text-slate-500">Loading dashboard…</div></Layout>;
  if (!data) return <Layout><div className="text-slate-500">Failed to load dashboard.</div></Layout>;

  const isSC1 = user?.role === "super_custodian" && user?.email === SC1_EMAIL;

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-600">
            {data.role === "super_custodian" ? "Super Custodian" : data.role === "hod" ? "Head of Delivery" : data.role === "tl" ? "Team Lead" : "Recruiter"}
            {isSC1 && viewAs !== "super_custodian" && (
              <span className="ml-2 text-amber-600">· Preview mode</span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">
            Good to see you, {user?.name?.split(" ")[0] || user?.email?.split("@")[0]}.
          </h1>
          <p className="text-slate-500 mt-2">
            {data.role === "recruiter"
              ? "Here are your candidates and follow-ups for today."
              : data.role === "tl"
              ? "Team activity and pipeline at a glance."
              : data.role === "hod"
              ? "Delivery org overview across your TL teams."
              : "Database overview, recruiter activity, and sync health."}
          </p>
        </div>

        {isSC1 && (
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-1" data-testid="role-switcher">
            <Eye className="w-4 h-4 text-slate-400 ml-2" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold pr-2">View as</span>
            {[
              { v: "super_custodian", label: "Admin" },
              { v: "hod", label: "HoD" },
              { v: "tl", label: "TL" },
              { v: "recruiter", label: "Recruiter" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`view-as-${opt.v}`}
                onClick={() => setViewAs(opt.v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  viewAs === opt.v ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {data.role === "super_custodian" && <><FollowUpsCard data={data} /><SuperCustodianView data={data} /></>}
      {data.role === "hod" && <><FollowUpsCard data={data} /><HoDView data={data} /></>}
      {data.role === "tl" && <><FollowUpsCard data={data} /><TLView data={data} /></>}
      {data.role === "recruiter" && <RecruiterView data={data} />}
    </Layout>
  );
}

function FollowUpsCard({ data }) {
  const [items, setItems] = useState(data.follow_ups || []);
  const [busy, setBusy] = useState(null);
  const total = data.follow_ups_total ?? items.length;
  useEffect(() => { setItems(data.follow_ups || []); }, [data.follow_ups]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/candidates/${id}/resolve-followup`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Follow-up resolved");
    } catch { toast.error("Could not resolve follow-up"); }
    finally { setBusy(null); }
  };

  if (total === 0) return null;
  return (
    <Card className="p-6 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white mb-6" data-testid="dash-followups-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-indigo-600" />
          <h3 className="font-display font-bold text-lg text-slate-900">Follow-ups due today</h3>
        </div>
        <Link to="/follow-ups" data-testid="all-followups-link" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
          All follow-ups ({total}) →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.slice(0, 3).map((c) => (
          <div key={c.id} data-testid={`followup-${c.id}`} className="p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all">
            <Link to={`/candidate/${c.id}`} className="block">
              <div className="font-semibold text-slate-900 truncate">{c.candidate_name}</div>
              <div className="text-xs text-slate-500 truncate">{(Array.isArray(c.domain) ? c.domain.join(", ") : c.domain) || "—"} · {c.current_city || "—"}</div>
              <div className="text-xs italic text-indigo-600 mt-1">
                {c.follow_up_overdue_days === 0 ? "Follow up today" : `Overdue by ${c.follow_up_overdue_days} day${c.follow_up_overdue_days === 1 ? "" : "s"}`}
              </div>
            </Link>
            <div className="mt-2 pt-2 border-t border-slate-100">
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-emerald-700 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`resolve-followup-${c.id}`}
                  disabled={busy === c.id}
                  onChange={() => resolve(c.id)}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Resolve
              </label>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SuperCustodianView({ data }) {
  const delta = data.added_this_week - data.added_last_week;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-total-candidates" icon={Database} label="Total candidates" value={data.total_candidates.toLocaleString()} sub="across all recruiters" />
        <Stat testid="stat-active-recruiters" icon={Users} label="Active recruiters" value={data.active_recruiters} accent="violet" sub="approved emails" />
        <Stat testid="stat-syncs-today" icon={RefreshCw} label="Syncs today" value={data.syncs_today} accent="emerald" sub={`${data.syncs_this_week} this week`} />
        <Stat testid="stat-duplicates" icon={AlertCircle} label="Duplicates rejected" value={data.duplicates_this_week} accent="amber" sub="this week" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-lg text-slate-900">Recent sync activity</h3>
              <p className="text-xs text-slate-500 mt-0.5">Latest 5 syncs across all recruiters</p>
            </div>
            <Link to="/sync-log" data-testid="link-sync-log" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {data.recent_syncs.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No syncs yet.</div>}
            {data.recent_syncs.map((s) => (
              <div key={s.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-900">{s.by}</div>
                  <div className="text-xs text-slate-500">{formatDateTime(s.ts)}</div>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-emerald-600 font-medium">+{s.added} added</span>
                  <span className="text-indigo-600 font-medium">{s.updated} updated</span>
                  <span className="text-amber-600 font-medium">{s.rejected_count} rejected</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">Growth this week</h3>
          <p className="text-xs text-slate-500 mb-4">Candidates added vs last week</p>
          <div className="font-display text-4xl font-bold text-slate-900">{data.added_this_week}</div>
          <div className={`mt-2 text-sm flex items-center gap-1.5 ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {delta >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="font-medium">{delta >= 0 ? "+" : ""}{delta}</span>
            <span className="text-slate-500">vs last week ({data.added_last_week})</span>
          </div>
          <div className="mt-6 flex gap-2">
            <Link to="/users" data-testid="quick-manage-users" className="flex-1 text-center px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">Manage users</Link>
            <Link to="/sync-log" data-testid="quick-sync-log" className="flex-1 text-center px-3 py-2 text-sm border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 transition-colors">Sync log</Link>
          </div>
        </Card>
      </div>
    </>
  );
}

function HoDView({ data }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-hod-total-candidates" icon={Database} label="Team database size" value={data.total_candidates.toLocaleString()} sub="candidates by your team" />
        <Stat testid="stat-hod-added-week" icon={TrendingUp} label="Added this week" value={data.team_added_this_week} accent="emerald" />
        <Stat testid="stat-hod-tl-count" icon={Users} label="TLs under you" value={data.tl_count ?? 0} accent="violet" />
        <Stat testid="stat-hod-recruiter-count" icon={Users} label="Recruiters under you" value={data.recruiter_count ?? 0} accent="amber" />
      </div>

      <Card className="p-6 border-slate-200" data-testid="hod-recent-syncs">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">Recent sync activity</h3>
            <p className="text-xs text-slate-500 mt-0.5">Last 5 syncs across your TL teams</p>
          </div>
          <Link to="/sync-log" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
            View all <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {data.recent_syncs.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No team syncs yet.</div>}
          {data.recent_syncs.map((s) => (
            <div key={s.id} className="py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium text-slate-900">{s.by}</div>
                <div className="text-xs text-slate-500">{formatDateTime(s.ts)}</div>
              </div>
              <div className="flex gap-4 text-xs">
                <span className="text-emerald-600 font-medium">+{s.added} added</span>
                <span className="text-indigo-600 font-medium">{s.updated} updated</span>
                <span className="text-amber-600 font-medium">{s.rejected_count} rejected</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function TLView({ data }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Stat testid="stat-total-candidates-tl" icon={Database} label="Database size" value={data.total_candidates.toLocaleString()} />
        <Stat testid="stat-added-week" icon={TrendingUp} label="Team added this week" value={data.team_added_this_week} accent="emerald" />
        <Stat testid="stat-team-size" icon={Users} label="Team size" value={data.team_size ?? 0} accent="violet" />
      </div>

      <Card className="p-6 border-slate-200" data-testid="tl-recent-syncs">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">Recent sync activity</h3>
            <p className="text-xs text-slate-500 mt-0.5">Last 5 syncs by recruiters on your team</p>
          </div>
          <Link to="/sync-log" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
            View all <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {data.recent_syncs.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No team syncs yet.</div>}
          {data.recent_syncs.map((s) => (
            <div key={s.id} className="py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium text-slate-900">{s.by}</div>
                <div className="text-xs text-slate-500">{formatDateTime(s.ts)}</div>
              </div>
              <div className="flex gap-4 text-xs">
                <span className="text-emerald-600 font-medium">+{s.added} added</span>
                <span className="text-indigo-600 font-medium">{s.updated} updated</span>
                <span className="text-amber-600 font-medium">{s.rejected_count} rejected</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function RecruiterView({ data }) {
  const [busy, setBusy] = useState(null);
  const [items, setItems] = useState(data.follow_ups || []);
  const total = data.follow_ups_total ?? items.length;

  useEffect(() => { setItems(data.follow_ups || []); }, [data.follow_ups]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/candidates/${id}/resolve-followup`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Follow-up resolved");
    } catch {
      toast.error("Could not resolve follow-up");
    } finally { setBusy(null); }
  };

  return (
    <>
      <Toaster richColors position="top-right" />
      {/* Follow-ups — most prominent */}
      <Card className="p-6 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white mb-6" data-testid="followups-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-indigo-600" />
              <h3 className="font-display font-bold text-xl text-slate-900">Follow-ups due today</h3>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {total === 0 ? "You're all caught up. Nice work." : `Showing ${items.length} of ${total} — most overdue first.`}
            </p>
          </div>
          {total > 0 && (
            <Link to="/follow-ups" data-testid="all-followups-link" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              All follow-ups ({total}) →
            </Link>
          )}
        </div>
        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {items.slice(0, 3).map((c) => (
              <div key={c.id} data-testid={`followup-${c.id}`} className="p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all">
                <Link to={`/candidate/${c.id}`} className="block">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{c.candidate_name}</div>
                      <div className="text-xs text-slate-500 truncate">{(Array.isArray(c.domain) ? c.domain.join(", ") : c.domain) || "—"} · {c.current_city || "—"}</div>
                    </div>
                    <StatusBadge status={c.candidate_status} />
                  </div>
                  <div className="text-xs italic text-indigo-600 mt-2">
                    {c.follow_up_overdue_days === 0 ? "Follow up today" : `Overdue by ${c.follow_up_overdue_days} day${c.follow_up_overdue_days === 1 ? "" : "s"}`}
                    {c.next_follow_up && <span className="not-italic text-slate-500"> · due {formatDate(c.next_follow_up)}</span>}
                  </div>
                </Link>
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-emerald-700 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`resolve-followup-${c.id}`}
                      disabled={busy === c.id}
                      onChange={() => resolve(c.id)}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    Resolve
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Stat testid="stat-my-total" icon={Database} label="My candidates" value={data.my_total} />
        <Stat testid="stat-last-sync" icon={RefreshCw} label="Last sync" value={data.last_sync ? `+${data.last_sync.added}` : "—"} sub={data.last_sync ? formatDateTime(data.last_sync.ts) : "No syncs yet"} accent="emerald" />
        <Stat testid="stat-follow-ups" icon={CalendarClock} label="Follow-ups due" value={total} accent="amber" />
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">Candidate pipeline</h3>
            <p className="text-xs text-slate-500 mt-0.5">Counts match Search filters exactly.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(data.pipeline).map(([k, v]) => (
            <Link
              key={k}
              to={`/search?status=${encodeURIComponent(k)}`}
              className="p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-300 transition-colors"
              data-testid={`my-pipeline-${k.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <StatusBadge status={k} />
              <div className="font-display text-2xl font-bold text-slate-900 mt-2">{v}</div>
            </Link>
          ))}
        </div>
      </Card>
    </>
  );
}
